import { retry, type BaseQueryFn, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../flatbuffers/api/flatBufferBaseQuery";
import { isUnreadableReply, retryUnlessUnreadable } from "./retryConditions";

/** How many times an API sends a failed request again, unless it sets its own number. */
export const DEFAULT_MAX_RETRIES = 2;

/**
 * Builds the condition an API's retries follow from its retry budget:
 * `retryUnlessUnreadable` (the default) or `retryUnlessClientError`.
 */
export type RetryRule = typeof retryUnlessUnreadable;

/**
 * A base query that reports its failures as FetchBaseQueryErrors, as
 * fetchBaseQuery and the FlatBuffer base query do. `eachAttempt` adds one of
 * its own, so a base query with another error type must not get in.
 */
type FetchErrorBaseQuery = BaseQueryFn &
  BaseQueryFn<never, unknown, FetchBaseQueryError, never, unknown>;

/** A retry condition as an endpoint's `extraOptions.retryCondition` carries one. */
type RetryCondition = (error: FetchBaseQueryError, args: unknown, extraArgs: unknown) => boolean;

/** The attempts behind each error `withRetries` returned (see `attemptsMade`). */
const attemptsByError = new WeakMap<object, number>();

/**
 * How many times the request that ended in `error` was attempted (the first
 * send and every retry), or undefined for an error `withRetries` did not
 * return. RTK Query hands the base query's error object to the store and the
 * hooks unchanged, so a hook's `error` finds its count here.
 */
export function attemptsMade(error: unknown): number | undefined {
  return typeof error === "object" && error !== null ? attemptsByError.get(error) : undefined;
}

/**
 * One attempt of a request, as `retry` makes them: counted against the
 * request's own options object (which `retry` hands to every attempt), and
 * with an error the base query THROWS returned as a CUSTOM_ERROR.
 *
 * RTK Query's `retry` (1.9) gives up on a returned error once the retry rule
 * says so, but retries a thrown one for ever. fetchBaseQuery builds its
 * Request outside its own try block, so an unusable API address (a mistyped
 * port, say) is thrown, and without this the request would spin with no end
 * and no message. An abort is unaffected: fetchBaseQuery returns it as a
 * FETCH_ERROR.
 *
 * The wrapper takes and returns exactly what `baseQuery` does, plus a
 * FetchBaseQueryError, which `FetchErrorBaseQuery` guarantees is the base
 * query's own error type, hence the one assertion: TypeScript cannot spell
 * "the same function type".
 */
function eachAttempt<BaseQuery extends FetchErrorBaseQuery>(
  baseQuery: BaseQuery,
  attempts: WeakMap<object, number>,
): BaseQuery {
  const attempt: BaseQueryFn<unknown, unknown, unknown> = async (args, api, extraOptions) => {
    if (typeof extraOptions === "object" && extraOptions !== null) {
      attempts.set(extraOptions, (attempts.get(extraOptions) ?? 0) + 1);
    }
    try {
      return await baseQuery(args, api, extraOptions);
    } catch (thrown) {
      const error: FetchBaseQueryError = { status: "CUSTOM_ERROR", error: String(thrown) };
      return { error };
    }
  };
  return attempt as BaseQuery;
}

/**
 * The options one request runs with: a copy of the endpoint's, so its
 * attempts can be counted against it, and with any `retryCondition` of the
 * endpoint's own made to refuse an unreadable reply first. RTK Query lets an
 * endpoint's condition replace the API's rather than add to it, so without
 * this a new condition would quietly resend replies that cannot be read.
 */
function requestOptions<Options>(extraOptions: Options): Options {
  const options: { retryCondition?: RetryCondition } = { ...(extraOptions as object) };
  const endpointCondition = options.retryCondition;
  if (endpointCondition) {
    options.retryCondition = (error, args, extraArgs) =>
      !isUnreadableReply(error) && endpointCondition(error, args, extraArgs);
  }
  return options as Options;
}

/**
 * Wraps a base query in RTK Query's `retry` with the app's retry rule: a
 * failed request is sent again up to `maxRetries` times with RTK's
 * exponential backoff, except a reply that arrived and cannot be read, which
 * is final (see `isUnreadableReply`). Pass `retryUnlessClientError` as `rule`
 * to also show a 4xx at once. An error the base query throws is returned
 * rather than thrown, so it, too, is retried only up to `maxRetries`.
 *
 * An endpoint can narrow the rule with its own `extraOptions.maxRetries`, or
 * replace it with `extraOptions.retryCondition`; an unreadable reply is still
 * never resent. Each error returned records how many attempts it took
 * (`attemptsMade`).
 */
export function withRetries<BaseQuery extends FetchErrorBaseQuery>(
  baseQuery: BaseQuery,
  maxRetries = DEFAULT_MAX_RETRIES,
  rule: RetryRule = retryUnlessUnreadable,
) {
  const attempts = new WeakMap<object, number>();
  const retrying = retry(eachAttempt(baseQuery, attempts), {
    retryCondition: rule(maxRetries),
  });
  const request: typeof retrying = async (args, api, extraOptions) => {
    const options = requestOptions(extraOptions);
    const result = await retrying(args, api, options);
    if (typeof result.error === "object" && result.error !== null) {
      attemptsByError.set(result.error, attempts.get(options) ?? 1);
    }
    return result;
  };
  return request;
}

/**
 * The base query a LiftCloud API is built on: the FlatBuffer base query for
 * `baseUrl`, retried per `withRetries`. Build a new API's base query with this
 * rather than calling `retry` directly, so it gets the same rule.
 */
export function createRetryingBaseQuery(
  baseUrl: string,
  maxRetries = DEFAULT_MAX_RETRIES,
  rule: RetryRule = retryUnlessUnreadable,
) {
  return withRetries(createFlatBufferBaseQuery(baseUrl), maxRetries, rule);
}
